/**
 * AccountsPage — Administração → Contas de login (S3.8).
 *
 * Gerencia as contas do model User (login). Restrito a super_admin: o nav
 * esconde para os demais e o backend retorna 403 (mensagem amigável). Lista,
 * cria, edita (nome/role/tenant/status) e reseta senha. A senha temporária
 * aparece UMA vez (banner). Distinto de "Empresas" (TenantUser/membership).
 */
import { useCallback, useEffect, useState } from "react";
import {
  createAccount,
  listAccounts,
  listAccountTenants,
  resetAccountPassword,
  updateAccount,
} from "../lib/api.js";

const ROLES = ["super_admin", "tenant_admin", "noc", "field", "viewer"];

function fmtDate(s) {
  if (!s) return "—";
  try { return new Date(s).toLocaleString("pt-BR"); } catch { return s; }
}

export default function AccountsPage() {
  const [accounts, setAccounts] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [error, setError] = useState(null);
  const [modal, setModal] = useState(null); // "new" | "edit"
  const [editItem, setEditItem] = useState(null);
  const [cred, setCred] = useState(null); // { email, temp_password }
  const [busyId, setBusyId] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const [acc, tn] = await Promise.all([listAccounts(), listAccountTenants()]);
      setAccounts(acc.accounts || []);
      setTenants(tn.tenants || []);
      setError(null);
    } catch (e) {
      setError(e?.message || "Falha ao carregar contas.");
      setAccounts([]);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const tenantName = (id) =>
    tenants.find((t) => t.id === id)?.name || (id ? id.slice(0, 8) : "—");

  async function handleReset(acc) {
    if (!window.confirm(`Resetar a senha de ${acc.email}? Será gerada uma senha temporária nova.`)) return;
    setBusyId(acc.id);
    setError(null);
    try {
      const out = await resetAccountPassword(acc.id);
      setCred({ email: out.email, temp_password: out.temp_password });
    } catch (e) {
      setError(e?.message || "Falha ao resetar a senha.");
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="p-4 space-y-3">
      <div className="flex items-baseline justify-between gap-2 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-noc-text">Contas de login</h1>
          <p className="text-xs text-noc-muted">
            Usuários que acessam o NOC — email, papel, empresa e status.
          </p>
        </div>
        <button
          className="text-xs px-2.5 py-1 rounded border border-noc-accent/40
                     bg-noc-accent/15 text-noc-accent font-medium"
          onClick={() => { setEditItem(null); setModal("new"); }}
        >
          + Nova conta
        </button>
      </div>

      {error && (
        <div className="text-sm text-noc-err bg-noc-err/10 border border-noc-err/30
                        rounded-lg px-3 py-2">
          {error}
        </div>
      )}

      {cred && <TempCredBanner cred={cred} onClose={() => setCred(null)} />}

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-left uppercase tracking-wide text-noc-muted
                           border-b border-noc-border/40">
              <th className="py-1 pr-2">Email</th>
              <th className="py-1 pr-2">Nome</th>
              <th className="py-1 pr-2">Papel</th>
              <th className="py-1 pr-2">Empresa</th>
              <th className="py-1 pr-2">Status</th>
              <th className="py-1 pr-2">Criado</th>
              <th className="py-1 pr-2"></th>
            </tr>
          </thead>
          <tbody>
            {accounts === null && (
              <tr><td colSpan={7} className="py-3 text-noc-muted">Carregando…</td></tr>
            )}
            {accounts && accounts.length === 0 && (
              <tr><td colSpan={7} className="py-3 text-noc-muted">Nenhuma conta.</td></tr>
            )}
            {(accounts || []).map((a) => (
              <tr key={a.id} className="border-b border-noc-border/20">
                <td className="py-1.5 pr-2 font-mono break-all">{a.email}</td>
                <td className="py-1.5 pr-2">{a.full_name || "—"}</td>
                <td className="py-1.5 pr-2">{a.role}</td>
                <td className="py-1.5 pr-2">{a.tenant_name || tenantName(a.tenant_id)}</td>
                <td className="py-1.5 pr-2">
                  {a.is_active
                    ? <span className="text-noc-ok">ativo</span>
                    : <span className="text-noc-err">inativo</span>}
                </td>
                <td className="py-1.5 pr-2 text-noc-muted">{fmtDate(a.created_at)}</td>
                <td className="py-1.5 pr-2">
                  <div className="flex gap-1">
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-border hover:bg-noc-bg/50"
                      onClick={() => { setEditItem(a); setModal("edit"); }}
                    >
                      Editar
                    </button>
                    <button
                      className="text-[10px] px-1.5 py-0.5 rounded border
                                 border-noc-warn/30 text-noc-warn
                                 hover:bg-noc-warn/10 disabled:opacity-40"
                      disabled={busyId === a.id}
                      onClick={() => handleReset(a)}
                    >
                      {busyId === a.id ? "…" : "Reset senha"}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {modal && (
        <AccountFormModal
          mode={modal}
          item={editItem}
          tenants={tenants}
          onClose={() => setModal(null)}
          onSaved={(newCred) => { setModal(null); if (newCred) setCred(newCred); refresh(); }}
        />
      )}
    </div>
  );
}

function TempCredBanner({ cred, onClose }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      await navigator.clipboard.writeText(cred.temp_password);
      setCopied(true);
    } catch { /* clipboard indisponível */ }
  }
  return (
    <div className="rounded-lg border border-noc-ok/40 bg-noc-ok/10 px-3 py-2 space-y-1">
      <div className="text-sm text-noc-ok font-medium">
        Senha temporária gerada — copie agora (mostrada uma única vez):
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <span className="text-xs text-noc-muted">{cred.email}</span>
        <code className="text-sm font-mono bg-noc-bg border border-noc-border
                         rounded px-2 py-0.5 select-all">
          {cred.temp_password}
        </code>
        <button onClick={copy}
                className="text-[11px] px-2 py-0.5 rounded border border-noc-border
                           hover:bg-noc-bg/50">
          {copied ? "copiado ✓" : "copiar"}
        </button>
        <button onClick={onClose}
                className="text-[11px] px-2 py-0.5 rounded border border-noc-border
                           hover:bg-noc-bg/50 ml-auto">
          fechar
        </button>
      </div>
    </div>
  );
}

function AccountFormModal({ mode, item, tenants, onClose, onSaved }) {
  const isEdit = mode === "edit";
  const [email, setEmail] = useState(item?.email || "");
  const [fullName, setFullName] = useState(item?.full_name || "");
  const [role, setRole] = useState(item?.role || "viewer");
  const [tenantId, setTenantId] = useState(item?.tenant_id || tenants[0]?.id || "");
  const [isActive, setIsActive] = useState(item?.is_active ?? true);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setErr(null);
    if (!isEdit && (!email.trim() || !email.includes("@"))) {
      setErr("Informe um email válido.");
      return;
    }
    if (!tenantId) { setErr("Selecione uma empresa."); return; }
    setBusy(true);
    try {
      if (isEdit) {
        await updateAccount(item.id, {
          full_name: fullName.trim(),
          role,
          tenant_id: tenantId,
          is_active: isActive,
        });
        onSaved(null);
      } else {
        const out = await createAccount({
          email: email.trim(),
          full_name: fullName.trim() || null,
          role,
          tenant_id: tenantId,
        });
        onSaved({ email: out.email, temp_password: out.temp_password });
      }
    } catch (e2) {
      setErr(e2?.message || "Falha ao salvar.");
    } finally {
      setBusy(false);
    }
  }

  const inputCls =
    "w-full h-9 px-3 rounded-lg bg-noc-bg border border-noc-border " +
    "text-noc-text outline-none focus:border-noc-accent/60";

  return (
    <div className="fixed inset-0 bg-black/60 z-[80] flex items-center justify-center p-4"
         onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="w-full max-w-md rounded-xl border border-noc-border bg-noc-panel
                      p-5 space-y-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold text-noc-text">
            {isEdit ? "Editar conta" : "Nova conta"}
          </h2>
          <button onClick={onClose}
                  className="text-noc-muted hover:text-noc-text text-lg leading-none">×</button>
        </div>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <label className="text-xs text-noc-muted">Email</label>
            <input type="email" value={email} disabled={isEdit}
                   onChange={(e) => setEmail(e.target.value)}
                   className={inputCls + (isEdit ? " opacity-60" : "")}
                   placeholder="usuario@empresa.com" />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-noc-muted">Nome</label>
            <input value={fullName} onChange={(e) => setFullName(e.target.value)}
                   maxLength={120} className={inputCls} placeholder="Nome completo" />
          </div>
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <label className="text-xs text-noc-muted">Papel</label>
              <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls}>
                {ROLES.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
            <div className="space-y-1">
              <label className="text-xs text-noc-muted">Empresa</label>
              <select value={tenantId} onChange={(e) => setTenantId(e.target.value)} className={inputCls}>
                {tenants.length === 0 && <option value="">(sem empresas)</option>}
                {tenants.map((t) => <option key={t.id} value={t.id}>{t.name}</option>)}
              </select>
            </div>
          </div>
          {isEdit && (
            <label className="flex items-center gap-2 text-sm text-noc-text">
              <input type="checkbox" checked={isActive}
                     onChange={(e) => setIsActive(e.target.checked)} />
              Conta ativa
            </label>
          )}
          {!isEdit && (
            <p className="text-[11px] text-noc-muted">
              Uma senha temporária será gerada e exibida uma vez após criar.
            </p>
          )}
          {err && (
            <div className="text-sm text-noc-err bg-noc-err/10 border border-noc-err/30
                            rounded-lg px-3 py-2">{err}</div>
          )}
          <button type="submit" disabled={busy}
                  className="w-full h-10 rounded-lg bg-noc-accent text-white font-medium
                             hover:opacity-90 disabled:opacity-50">
            {busy ? "Salvando…" : (isEdit ? "Salvar" : "Criar conta")}
          </button>
        </form>
      </div>
    </div>
  );
}
